import type { TaskListResponse } from '../../services/api';
import { getAgencyTimezone } from '../../utils/agencyTimezone';
import { formatDateTimeInZone } from '../../utils/dateUtils';

/** One visit's patient-signature state, as every API surface reports it. */
export type PatientSignature = NonNullable<
  TaskListResponse['patient_signature']
>;

/**
 * Screen-reader and tooltip wording for one patient-signature state.
 *
 * Shared by the patient Tasks tab, Hotbox and the visit menu so the three
 * cannot describe the same visit differently. Payroll keeps its own longer
 * copy, which answers a payroll reviewer's "why".
 */
export function patientSignatureLabel(
  state: PatientSignature | null | undefined
): string {
  switch (state) {
    case 'signed':
      return 'Patient signature captured';
    case 'unable_to_sign':
      return 'Patient unable to sign';
    case 'previous_emr':
      return 'Signature stays in External EMR';
    default:
      return 'No patient signature captured';
  }
}

/**
 * The "Captured …" line a signed or unable-to-sign row's hover prints.
 * The formatter and zone are the EVV dialog's own, so the two surfaces
 * cannot name different times for one capture. Null when Roger holds
 * no live capture, so the hover shows the label alone.
 */
export function patientSignatureCapturedLine(
  state: PatientSignature | null | undefined,
  capturedAt: string | null | undefined
): string | null {
  if (state !== 'signed' && state !== 'unable_to_sign') return null;
  const captured = formatDateTimeInZone(capturedAt, getAgencyTimezone());
  return captured ? `Captured ${captured}` : null;
}

// Hotbox wording for a visit the viewer cannot capture: the own-visit
// label and its hint, and the hover text on another clinician's visit.
export const PATIENT_SIGNATURE_CANNOT_CAPTURE_LABEL = "Can't capture";
export const PATIENT_SIGNATURE_CANNOT_CAPTURE_HINT =
  "Your role can't capture patient signatures. Ask your agency admin for a role with patient-signature capture.";
export const PATIENT_SIGNATURE_OTHER_ASSIGNEE_TITLE =
  'Only the assigned clinician captures the patient signature.';
